import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { api } from "./client";

// --- Health ---
export function useHealth() {
  return useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 15_000,
    retry: 1,
  });
}

// --- Desks ---
export function useDesks(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ["desks"],
    queryFn: api.listDesks,
    refetchInterval: 5_000,
    enabled: opts?.enabled ?? true,
  });
}

export function useDesk(id: string) {
  return useQuery({
    queryKey: ["desk", id],
    queryFn: () => api.getDesk(id),
    refetchInterval: 3_000,
    enabled: !!id,
  });
}

export function useCreateDesk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.createDesk,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["desks"] }),
  });
}

export function useDeleteDesk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteDesk(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["desks"] }),
  });
}

export function useWakeDesk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.wakeDesk(id),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["desk", id] });
      qc.invalidateQueries({ queryKey: ["desks"] });
    },
  });
}

// --- Files ---
export function useFiles(
  deskId: string,
  dirPath: string,
  autoRefresh?: boolean,
  root?: "data" | "sprite",
) {
  return useQuery({
    queryKey: ["files", deskId, dirPath, root ?? "data"],
    queryFn: () => api.listFiles(deskId, dirPath, root),
    enabled: !!deskId,
    refetchInterval: autoRefresh ? 5_000 : false,
  });
}

export function useUploadFile(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ dirPath, file }: { dirPath: string; file: File }) =>
      api.uploadFile(deskId, dirPath, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files", deskId] }),
  });
}

export function useMkdir(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dirPath: string) => api.mkdir(deskId, dirPath),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files", deskId] }),
  });
}

export function useDeleteFile(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (filePath: string) => api.deleteFile(deskId, filePath),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["files", deskId] }),
  });
}

// --- Sessions ---
export function useSessions(deskId: string) {
  return useQuery({
    queryKey: ["sessions", deskId],
    queryFn: () => api.listSessions(deskId),
    refetchInterval: 3_000,
    enabled: !!deskId,
  });
}

export function useSessionInfo(deskId: string, sessionId: string | null) {
  return useQuery({
    queryKey: ["session", deskId, sessionId],
    queryFn: () => api.getSessionInfo(deskId, sessionId as string),
    refetchInterval: 2_000,
    enabled: !!deskId && !!sessionId,
  });
}

export function useEvents(
  deskId: string,
  sessionId: string | null,
  isActive?: boolean,
) {
  return useQuery({
    queryKey: ["events", deskId, sessionId],
    queryFn: () => api.getEvents(deskId, sessionId as string),
    refetchInterval: isActive ? 1_000 : 5_000,
    enabled: !!deskId && !!sessionId,
  });
}

// --- Agent ---
export function useStartAgent(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (opts: {
      prompt: string;
      invocation?: string;
      model?: string;
      systemPrompt?: string;
    }) => api.startAgent(deskId, opts),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["sessions", deskId] });
      qc.invalidateQueries({ queryKey: ["session", deskId, data.sessionId] });
      qc.invalidateQueries({ queryKey: ["events", deskId, data.sessionId] });
    },
  });
}

export function useSendMessage(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sessionId,
      prompt,
      model,
    }: {
      sessionId: string;
      prompt: string;
      model?: string;
    }) => api.sendMessage(deskId, sessionId, prompt, model),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["sessions", deskId] });
      qc.invalidateQueries({ queryKey: ["session", deskId, vars.sessionId] });
      qc.invalidateQueries({ queryKey: ["events", deskId, vars.sessionId] });
    },
  });
}

export function useDeleteSession(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.deleteSession(deskId, sessionId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["sessions", deskId] }),
  });
}

export function useInterruptAgent(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sessionId: string) => api.interruptAgent(deskId, sessionId),
    onSuccess: (_, sessionId) => {
      qc.invalidateQueries({ queryKey: ["sessions", deskId] });
      qc.invalidateQueries({ queryKey: ["events", deskId, sessionId] });
    },
  });
}

// --- Env vars ---
export function useEnvVars(deskId: string) {
  return useQuery({
    queryKey: ["env", deskId],
    queryFn: () => api.listEnvVars(deskId),
    enabled: !!deskId,
  });
}

export function useAddEnvVar(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      api.addEnvVar(deskId, key, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["env", deskId] }),
  });
}

export function useDeleteEnvVar(deskId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => api.deleteEnvVar(deskId, key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["env", deskId] }),
  });
}

/**
 * Presence ping: while the desk view is mounted we POST to the orchestrator's
 * /presence endpoint every 8 seconds. The orchestrator uses this to
 * keepalive-ping the sprite on our behalf (via its own reliable exec
 * mechanism) so the user doesn't hit a cold-start when they start typing.
 *
 * Pinging through the orchestrator rather than the public relay URL is
 * deliberate: the orchestrator's ping goes via the sprite exec path which
 * sprites.dev recognizes as activity, while direct public-URL fetches
 * don't always prevent idle.
 *
 * `data` reflects whether the last presence ping succeeded — callers can
 * use it as a liveness hint (shown as "relay unreachable" in the top bar
 * when false).
 */
export function useDeskPresence(deskId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["presence", deskId],
    queryFn: async () => {
      try {
        await api.presence(deskId);
        return true;
      } catch {
        return false;
      }
    },
    refetchInterval: 8_000,
    enabled: enabled && !!deskId,
    retry: false,
    staleTime: 5_000,
  });
}

/**
 * @deprecated Use useDeskPresence instead. Kept as a thin compat wrapper
 * so any remaining imports don't break; it now routes through presence.
 */
export function useDeskKeepalive(
  deskId: string,
  _relayUrl: string | null,
  enabled: boolean,
) {
  return useDeskPresence(deskId, enabled);
}

/**
 * Returns a prefetch function that warms the queries the desk detail view
 * will need (desk, sessions, root files). Safe to call repeatedly; TanStack
 * Query dedupes and respects staleTime.
 *
 * Use from the dashboard on hover / touchstart to make desk navigation feel
 * instant — the desk detail route usually renders with data already in cache.
 */
export function usePrefetchDesk() {
  const qc = useQueryClient();
  return useCallback(
    (deskId: string) => {
      qc.prefetchQuery({
        queryKey: ["desk", deskId],
        queryFn: () => api.getDesk(deskId),
        staleTime: 3_000,
      });
      qc.prefetchQuery({
        queryKey: ["sessions", deskId],
        queryFn: () => api.listSessions(deskId),
        staleTime: 3_000,
      });
      qc.prefetchQuery({
        queryKey: ["files", deskId, "/", "data"],
        queryFn: () => api.listFiles(deskId, "/"),
        staleTime: 3_000,
      });
    },
    [qc],
  );
}

// --- Spend ---
export function useUserSpend() {
  return useQuery({
    queryKey: ["spend", "user"],
    queryFn: api.userSpend,
    refetchInterval: 30_000,
  });
}
